import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Printer, ShieldCheck, CheckCircle2, QrCode } from 'lucide-react';
import { Button } from './ui/button';
import { generateReceipt, angkaKeTerbilang, createStampDataUrl } from '@/utils/pdfReceipt';
import logoBadgeImg from '@/assets/images/azman_neon_badge_1791095661065.jpg';

interface ReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: any;
  userName: string;
  userEmail: string;
}

export default function ReceiptModal({
  isOpen,
  onClose,
  order,
  userName,
  userEmail
}: ReceiptModalProps) {
  const printableRef = useRef<HTMLDivElement>(null);

  if (!isOpen || !order) return null;

  const isCompleted = order.status === 'completed';
  const orderDate = order?.createdAt?.toDate
    ? order.createdAt.toDate()
    : order?.createdAt?.seconds
    ? new Date(order.createdAt.seconds * 1000)
    : new Date();

  const formattedDate = orderDate.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const formattedShortDate = orderDate.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  const receiptNo = `KWT-${orderDate.getFullYear()}${String(orderDate.getMonth() + 1).padStart(2, '0')}-${(order?.id || '0000').slice(-6).toUpperCase()}`;
  const amount = Number(order?.amount || 0);
  const amountFormatted = `Rp ${amount.toLocaleString('id-ID')}`;
  const terbilangText = angkaKeTerbilang(amount);

  const stampUrl = createStampDataUrl(formattedShortDate, isCompleted, receiptNo);

  const handleDownloadPdf = () => {
    generateReceipt(order, userName, userEmail);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-sm overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          className="relative w-full max-w-3xl my-6 bg-background rounded-2xl shadow-2xl border border-border flex flex-col max-h-[92vh]"
        >
          {/* Top Bar Action */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-card/60 backdrop-blur rounded-t-2xl">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                <ShieldCheck className="h-4 w-4 text-emerald-500" />
              </div>
              <div>
                <h3 className="font-bold text-sm sm:text-base text-foreground">
                  {isCompleted ? 'Kuitansi Resmi Pembayaran' : 'Invoice Penagihan'}
                </h3>
                <p className="text-xs text-muted-foreground">
                  No: <span className="font-mono font-medium text-foreground">{receiptNo}</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleDownloadPdf}
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-sm text-xs h-8 px-3 gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                <span>Unduh PDF</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Scrollable Receipt Body (Paper Aesthetic) */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-8 bg-slate-100 dark:bg-slate-900/60">
            <div
              ref={printableRef}
              className="max-w-2xl mx-auto bg-white text-slate-900 shadow-xl rounded-xl border border-slate-200 overflow-hidden relative"
              style={{
                fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
              }}
            >
              {/* Top Colored Stripe */}
              <div className="h-2 bg-[#0B192C]" />
              <div className="h-1 bg-[#D4AF37]" />

              <div className="p-6 sm:p-8 space-y-6">
                {/* Header / Kop Surat */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-5 border-b-2 border-slate-200">
                  <div className="flex items-center gap-3.5">
                    <img 
                      src={logoBadgeImg} 
                      alt="Logo AZMAN 204" 
                      className="h-14 w-14 rounded-2xl object-contain shadow-md shrink-0 border border-slate-200/80 bg-white" 
                    />
                    <div>
                      <h2 className="text-xl font-extrabold tracking-tight text-[#0B192C]">
                        AZMAN204
                      </h2>
                      <p className="text-[11px] font-bold text-slate-600 tracking-wide">
                        PORTAL ADMINISTRASI & KONSULTASI JASA KONSTRUKSI
                      </p>
                      <p className="text-[10px] text-slate-500">
                        Jl. Soekarno-Hatta , Anambas, Riau, Indonesia • Telp/WA: +62 812-6108-9400
                      </p>
                    </div>
                  </div>

                  <div className="text-left sm:text-right shrink-0">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-xs">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span>{isCompleted ? 'LUNAS (TERVERIFIKASI)' : 'BELUM DIBAYAR'}</span>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1 font-mono">
                      {receiptNo}
                    </p>
                  </div>
                </div>

                {/* Receipt Title */}
                <div className="text-center py-1">
                  <h1 className="text-lg sm:text-xl font-extrabold text-[#0B192C] tracking-wide uppercase">
                    {isCompleted ? 'Kuitansi Pembayaran Resmi' : 'Invoice Penagihan'}
                  </h1>
                  <p className="text-[11px] text-slate-500 font-medium">
                    {isCompleted ? 'OFFICIAL PAYMENT RECEIPT' : 'OFFICIAL PAYMENT INVOICE'}
                  </p>
                </div>

                {/* Metadata Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs">
                  <div className="space-y-1">
                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                      Telah Diterima Dari
                    </span>
                    <p className="font-bold text-sm text-slate-900">
                      {userName || order.userName || 'Pelanggan Terdaftar'}
                    </p>
                    <p className="text-slate-600">{userEmail || order.userEmail || '-'}</p>
                    <p className="text-slate-500">Telp: {order.userPhone || '-'}</p>
                  </div>

                  <div className="space-y-1 sm:border-l sm:border-slate-200 sm:pl-4">
                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                      Rincian Transaksi
                    </span>
                    <div className="flex justify-between py-0.5">
                      <span className="text-slate-500">No. Dokumen:</span>
                      <span className="font-mono font-semibold text-slate-800">{receiptNo}</span>
                    </div>
                    <div className="flex justify-between py-0.5">
                      <span className="text-slate-500">Tanggal:</span>
                      <span className="font-medium text-slate-800">{formattedDate}</span>
                    </div>
                    <div className="flex justify-between py-0.5">
                      <span className="text-slate-500">Metode:</span>
                      <span className="font-medium text-slate-800">Transfer Bank / QRIS</span>
                    </div>
                  </div>
                </div>

                {/* Table of Items */}
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-[#0B192C] text-white font-semibold">
                        <th className="py-2.5 px-3 text-center w-10">NO</th>
                        <th className="py-2.5 px-3">DESKRIPSI PRODUK / LAYANAN</th>
                        <th className="py-2.5 px-3 text-center">FORMAT</th>
                        <th className="py-2.5 px-3 text-center w-12">QTY</th>
                        <th className="py-2.5 px-3 text-right">TOTAL</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 text-slate-700">
                      <tr className="hover:bg-slate-50">
                        <td className="py-3 px-3 text-center font-medium">1</td>
                        <td className="py-3 px-3 font-medium text-slate-900">
                          {order.productName || 'Paket Dokumen Konstruksi'}
                        </td>
                        <td className="py-3 px-3 text-center text-slate-500">Digital (.doc/.xls/PDF)</td>
                        <td className="py-3 px-3 text-center">1</td>
                        <td className="py-3 px-3 text-right font-bold text-slate-900">
                          {amountFormatted}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Total & Terbilang */}
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                  {/* Terbilang */}
                  <div className="sm:col-span-7 p-3.5 rounded-lg bg-amber-50/70 border border-amber-200/80">
                    <span className="text-[10px] uppercase font-bold text-amber-900 tracking-wider">
                      Terbilang:
                    </span>
                    <p className="text-xs font-bold italic text-amber-950 mt-1 uppercase">
                      # {terbilangText} #
                    </p>
                  </div>

                  {/* Total Box */}
                  <div className="sm:col-span-5 p-3.5 rounded-lg bg-slate-50 border border-slate-200 space-y-1 text-xs">
                    <div className="flex justify-between text-slate-500">
                      <span>Subtotal:</span>
                      <span>{amountFormatted}</span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span>Biaya Admin:</span>
                      <span>Rp 0</span>
                    </div>
                    <div className="pt-2 border-t border-slate-300 flex justify-between font-bold text-sm text-[#0B192C]">
                      <span>Grand Total:</span>
                      <span className="text-emerald-600 font-extrabold">{amountFormatted}</span>
                    </div>
                  </div>
                </div>

                {/* Official Stamp & Sign Section */}
                <div className="pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-6 items-end">
                  {/* Digital QR & Security */}
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg shadow-xs">
                      <QrCode className="h-14 w-14 text-[#0B192C]" />
                    </div>
                    <div className="text-[10px] text-slate-500 space-y-0.5">
                      <p className="font-bold text-slate-800">KEABSAHAN DIGITAL</p>
                      <p>Pindai QR untuk memverifikasi keaslian kuitansi di portal resmi.</p>
                      <p className="font-mono text-[9px] text-slate-400">ID: {order.id}</p>
                    </div>
                  </div>

                  {/* Stamp & Authorized Signature */}
                  <div className="text-right sm:text-center sm:ml-auto w-full sm:w-56 relative flex flex-col items-center">
                    <p className="text-[11px] text-slate-500">Anambas, {formattedDate}</p>
                    <p className="text-[11px] font-bold text-[#0B192C]">
                      Azman204 Finance & Administration
                    </p>

                    {/* Stamp & Signature Container */}
                    <div className="h-20 w-full relative flex items-center justify-center my-1">
                      {/* Physical Wet Stamp Badge */}
                      {stampUrl && (
                        <img
                          src={stampUrl}
                          alt="Stempel Lunas"
                          className="absolute -top-1 w-24 h-24 object-contain opacity-90 drop-shadow-sm select-none pointer-events-none transform -rotate-3"
                        />
                      )}
                      {/* Signature graphic */}
                      <svg className="w-32 h-14 text-slate-800 relative z-0" viewBox="0 0 200 80" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M 20 55 Q 40 15 65 25 T 95 65 Q 120 75 140 30 T 175 45" />
                        <path d="M 45 45 L 160 45" />
                        <path d="M 30 65 L 150 62" />
                      </svg>
                    </div>

                    <div className="border-t border-slate-900 pt-1 w-44 text-center">
                      <p className="text-xs font-bold text-[#0B192C]">AZMAN</p>
                      <p className="text-[9px] text-slate-500">Head of Technical & Licensing</p>
                    </div>
                  </div>
                </div>

                {/* Footer Legal */}
                <div className="pt-4 border-t border-slate-200 text-center text-[9px] text-slate-400 italic">
                  Kuitansi ini dicetak secara otomatis dan sah secara hukum berdasarkan UU ITE No. 11/2008. Simpan dokumen ini sebagai bukti transaksi resmi.
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Action Footer */}
          <div className="px-5 py-3.5 border-t border-border bg-card/60 backdrop-blur rounded-b-2xl flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="text-xs h-9 gap-1.5"
            >
              <Printer className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Cetak Kuitansi</span>
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-xs h-9 text-muted-foreground hover:text-foreground"
              >
                Tutup
              </Button>
              <Button
                size="sm"
                onClick={handleDownloadPdf}
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-xs h-9 px-4 gap-1.5 shadow-sm"
              >
                <Download className="h-3.5 w-3.5" />
                <span>Unduh PDF Resmi (.pdf)</span>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
